import { forwardRef, Ref, useCallback, useContext } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { motion } from 'motion/react';
import { Link as RouterLink, To } from 'react-router';

import { MenuContext } from '@components/primitives/Menu/context';
import { Spinner } from '@components/primitives/Spinner';
import { cn } from '@components/primitives/utilities';

import styles from './styles.module.css';

const MotionLink = motion.create(RouterLink);

export type MenuItemProps = DropdownMenu.DropdownMenuItemProps & {
  variant?: 'danger';
  icon?: React.ReactNode;
  loading?: boolean;
  href?: To;
  state?: any;
};

export const MenuItem = forwardRef<
  HTMLDivElement | HTMLAnchorElement,
  MenuItemProps
>(function MenuItem(
  {
    className,
    variant,
    children,
    icon,
    onSelect,
    loading,
    href,
    state,
    ...props
  },
  ref
) {
  const [, setOpen] = useContext(MenuContext);

  const handleSelect = useCallback(
    (e: Event) => {
      setOpen(false);
      onSelect?.(e);
    },
    [onSelect, setOpen]
  );

  const sharedProps = {
    className: cn(styles.menuItem, className),
    'data-variant': variant,
    exit: { opacity: 0.99 },
    transition: { duration: 0.2 },
  };

  return (
    <DropdownMenu.Item
      asChild
      onSelect={handleSelect}
      {...props}
      disabled={props.disabled || loading}
    >
      {href ? (
        <MotionLink
          ref={ref as Ref<HTMLAnchorElement>}
          to={href}
          state={state}
          {...sharedProps}
        >
          {(loading || icon) && (
            <span className={styles.icon} aria-hidden>
              {loading ? <Spinner /> : icon}
            </span>
          )}
          <span className={styles.label}>{children}</span>
        </MotionLink>
      ) : (
        <motion.div ref={ref as Ref<HTMLDivElement>} {...sharedProps}>
          {(loading || icon) && (
            <span className={styles.icon} aria-hidden>
              {loading ? <Spinner /> : icon}
            </span>
          )}
          <span className={styles.label}>{children}</span>
        </motion.div>
      )}
    </DropdownMenu.Item>
  );
});
